"use client"

import { useEffect, useRef } from "react"
import { ATLAS_LAND_MASK } from "./atlas-globe-land"

/**
 * Decorative dot globe for pages outside a workspace, drawn in the style of
 * the ortelian.com atlas globe. It turns slowly with a pin on Antwerp and
 * holds still while the reader prefers reduced motion. Dots take the brand
 * color.
 */
export function AtlasGlobe({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas?.getContext("2d")
    if (!canvas || !ctx) return

    const geometry = globeGeometry()
    const ink = getComputedStyle(canvas).getPropertyValue("--color-kumo-brand").trim() || "currentColor"
    const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)")
    let width = 0
    let height = 0
    let frame = 0
    let lastDraw = 0
    let start = performance.now()

    const draw = (seconds: number, still: boolean) => {
      const spin = (ANTWERP.lon - 20 + seconds * SPIN_DEGREES_PER_SECOND) * DEG
      const cosSpin = Math.cos(spin)
      const sinSpin = Math.sin(spin)
      const radius = Math.min(width, height) * 0.46
      const cx = width / 2
      const cy = height / 2
      // Rotates a unit vector by the spin, then tilts it toward the reader.
      const project = (vectors: Float32Array, i: number) => {
        const front = vectors[i] * cosSpin + vectors[i + 1] * sinSpin
        const side = vectors[i + 1] * cosSpin - vectors[i] * sinSpin
        const up = vectors[i + 2]
        PROJECTED.x = cx + radius * side
        PROJECTED.y = cy - radius * (COS_TILT * up - SIN_TILT * front)
        PROJECTED.z = SIN_TILT * up + COS_TILT * front
        return PROJECTED
      }

      ctx.clearRect(0, 0, width, height)
      ctx.fillStyle = ink
      ctx.strokeStyle = ink
      ctx.lineWidth = 1

      ctx.globalAlpha = 0.16
      ctx.beginPath()
      ctx.arc(cx, cy, radius, 0, Math.PI * 2)
      ctx.stroke()

      ctx.globalAlpha = 0.07
      ctx.beginPath()
      for (const line of geometry.graticule) {
        let drawing = false
        for (let i = 0; i < line.length; i += 3) {
          const p = project(line, i)
          if (p.z > 0) {
            if (drawing) ctx.lineTo(p.x, p.y)
            else ctx.moveTo(p.x, p.y)
            drawing = true
          } else {
            drawing = false
          }
        }
      }
      ctx.stroke()

      // One path per depth band keeps canvas calls to a handful per frame.
      const land = Array.from({ length: LAND_BANDS }, () => new Path2D())
      const ocean = Array.from({ length: OCEAN_BANDS }, () => new Path2D())
      const { vectors, land: isLand } = geometry
      for (let i = 0, point = 0; i < vectors.length; i += 3, point++) {
        const p = project(vectors, i)
        if (p.z <= 0) continue
        if (isLand[point]) {
          land[Math.min(LAND_BANDS - 1, Math.floor(p.z * LAND_BANDS))].rect(p.x - 1.2, p.y - 1.2, 2.4, 2.4)
        } else {
          ocean[Math.min(OCEAN_BANDS - 1, Math.floor(p.z * OCEAN_BANDS))].rect(p.x - 0.7, p.y - 0.7, 1.4, 1.4)
        }
      }
      land.forEach((path, band) => {
        ctx.globalAlpha = 0.9 * depthShade((band + 0.5) / LAND_BANDS)
        ctx.fill(path)
      })
      ocean.forEach((path, band) => {
        ctx.globalAlpha = 0.1 * depthShade((band + 0.5) / OCEAN_BANDS)
        ctx.fill(path)
      })

      const pin = project(geometry.antwerp, 0)
      if (pin.z > 0) {
        const pulse = still ? 0 : (seconds % 2.4) / 2.4
        ctx.globalAlpha = 0.7 * (1 - pulse)
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.arc(pin.x, pin.y, 5 + 16 * pulse, 0, Math.PI * 2)
        ctx.stroke()
        ctx.globalAlpha = 1
        ctx.beginPath()
        ctx.arc(pin.x, pin.y, 4, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick)
      if (now - lastDraw < FRAME_INTERVAL_MS) return
      lastDraw = now
      draw((now - start) / 1000, false)
    }

    const play = () => {
      cancelAnimationFrame(frame)
      if (reducedMotion.matches) {
        draw(0, true)
        return
      }
      start = performance.now()
      lastDraw = 0
      frame = requestAnimationFrame(tick)
    }

    const resize = () => {
      const ratio = Math.min(devicePixelRatio || 1, 2)
      width = canvas.clientWidth
      height = canvas.clientHeight
      canvas.width = Math.round(width * ratio)
      canvas.height = Math.round(height * ratio)
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
      if (reducedMotion.matches) draw(0, true)
      else lastDraw = 0
    }

    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    resize()
    play()
    reducedMotion.addEventListener("change", play)
    return () => {
      observer.disconnect()
      reducedMotion.removeEventListener("change", play)
      cancelAnimationFrame(frame)
    }
  }, [])

  return <canvas ref={ref} aria-hidden className={className} />
}

const DEG = Math.PI / 180
const TILT = 28 * DEG
const SIN_TILT = Math.sin(TILT)
const COS_TILT = Math.cos(TILT)
const SPIN_DEGREES_PER_SECOND = 2.4
// The globe turns about 0.1° a frame at this rate, so 30 frames a second
// looks smooth and leaves the main thread free for the form.
const FRAME_INTERVAL_MS = 1000 / 30
const ANTWERP = { lat: 51.05, lon: 3.72 }
const GOLDEN_ANGLE_DEGREES = 180 * (3 - Math.sqrt(5))
const DENSE_POINTS = 20000
const LAND_BANDS = 6
const OCEAN_BANDS = 3
const PROJECTED = { x: 0, y: 0, z: 0 }

function depthShade(z: number) {
  return 0.35 + 0.65 * z
}

/** Latitude and longitude in degrees. */
type LatLon = { lat: number; lon: number }

type GlobeGeometry = {
  /** Unit vectors, three numbers per point. */
  vectors: Float32Array
  land: Uint8Array
  graticule: Float32Array[]
  antwerp: Float32Array
}
let cachedGeometry: GlobeGeometry | undefined

function fibonacciSphere(count: number): LatLon[] {
  return Array.from({ length: count }, (_, i) => ({
    lat: Math.asin(1 - ((i + 0.5) / count) * 2) / DEG,
    lon: ((((i * GOLDEN_ANGLE_DEGREES) % 360) + 540) % 360) - 180,
  }))
}

function unitVector({ lat, lon }: LatLon): [number, number, number] {
  const cosLat = Math.cos(lat * DEG)
  return [cosLat * Math.cos(lon * DEG), cosLat * Math.sin(lon * DEG), Math.sin(lat * DEG)]
}

function vectorsOf(points: LatLon[]) {
  return Float32Array.from(points.flatMap(unitVector))
}

// The mask holds one land bit per point of a Fibonacci sphere. A denser
// lattice takes the land bit of its nearest mask point so coastlines read at
// full-page size.
function globeGeometry(): GlobeGeometry {
  if (cachedGeometry) return cachedGeometry
  const bits = Uint8Array.from(atob(ATLAS_LAND_MASK), (c) => c.charCodeAt(0))
  const cells = new Map<string, { v: [number, number, number]; land: number }[]>()
  const cellKey = ({ lat, lon }: LatLon) =>
    `${Math.floor((lat + 90) / 6)}:${((Math.floor((lon + 180) / 6) % 60) + 60) % 60}`
  fibonacciSphere(bits.length * 8).forEach((point, i) => {
    const key = cellKey(point)
    const list = cells.get(key) ?? []
    list.push({ v: unitVector(point), land: (bits[i >> 3] >> (i & 7)) & 1 })
    cells.set(key, list)
  })

  const dense = fibonacciSphere(DENSE_POINTS)
  const land = new Uint8Array(dense.length)
  dense.forEach((point, index) => {
    const v = unitVector(point)
    let best = Infinity
    for (let dLat = -6; dLat <= 6; dLat += 6) {
      for (let dLon = -12; dLon <= 12; dLon += 6) {
        for (const c of cells.get(cellKey({ lat: point.lat + dLat, lon: point.lon + dLon })) ?? []) {
          const d = (v[0] - c.v[0]) ** 2 + (v[1] - c.v[1]) ** 2 + (v[2] - c.v[2]) ** 2
          if (d < best) {
            best = d
            land[index] = c.land
          }
        }
      }
    }
  })

  const parallels = range(-75, 75, 15).map((lat) => vectorsOf(range(-180, 180, 3).map((lon) => ({ lat, lon }))))
  const meridians = range(-180, 165, 15).map((lon) => vectorsOf(range(-90, 90, 3).map((lat) => ({ lat, lon }))))
  cachedGeometry = {
    vectors: vectorsOf(dense),
    land,
    graticule: [...parallels, ...meridians],
    antwerp: vectorsOf([ANTWERP]),
  }
  return cachedGeometry
}

function range(from: number, to: number, step: number) {
  return Array.from({ length: Math.floor((to - from) / step) + 1 }, (_, i) => from + i * step)
}
